// src/components/GuestLogin.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Divider,
  Row,
  Col,
} from 'antd';
import { 
  LockOutlined, 
  UserOutlined, 
  IdcardOutlined, 
  KeyOutlined, 
  MailOutlined,
  PhoneOutlined 
} from '@ant-design/icons';
import axiosInstance from '../api/axiosInstance';
import notify from "../utils/notify";

const { Title, Text } = Typography;

const GuestLogin = ({ visible, onClose, onLoginSuccess, hotelId }) => {
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!visible) {
      // Reset form when modal closes
      form.resetFields();
      setError('');
      setLoginAttempts(0);
      setForgotPasswordMode(false);
    }
  }, [visible, form]);

  const hid = Number(hotelId);
if (!Number.isFinite(hid)) {
  notify.error('Hotel is not set correctly. Please refresh or contact reception.');
  return;
}

  // In GuestLogin.jsx - Update the onFinish function
const onFinish = async (values) => {
  setLoading(true);
  setError('');

  try {
    //console.log('🔐 Login attempt:', { 
    //  idNumber: values.idNumber, 
     // hotelId,
     // passwordLength: values.password?.length 
    //});
    
    const response = await axiosInstance.post('/GuestLogin/login', {
      idNumber: values.idNumber,
      password: values.password,
      hotelId: Number(hotelId),
    });

    //console.log('✅ Login response:', response.data);

    if (response.data.success) {
      //console.log('🚀 Login successful, calling onLoginSuccess');
      
      // Store token and guest info
      localStorage.setItem('guestToken', response.data.accessToken);
      localStorage.setItem('guestInfo', JSON.stringify(response.data.guest));
      
      notify.success('Login successful!');
      
      // Ensure we're passing the correct data structure
      onLoginSuccess?.({
        accessToken: response.data.accessToken,
        guest: response.data.guest
      });
      
      onClose?.();
    } else {
      //console.log('❌ Login response success=false');
      setError(response.data.message || 'Login failed');
    }
  } catch (err) {
    console.error('💥 Login error:', {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message
    });
    
    const attempts = loginAttempts + 1;
    setLoginAttempts(attempts);
    
    if (err.response?.status === 423) {
      setError('Account locked. Please try again later or contact reception.');
    } else if (attempts >= 3) {
      setError('Too many failed attempts. Please contact reception.');
    } else {
      const errorMsg = err.response?.data?.message || 'Login failed. Please check your credentials.';
      const attemptsLeft = err.response?.data?.attemptsLeft;
      
      if (attemptsLeft) {
        setError(`${errorMsg} (${attemptsLeft} attempts remaining)`);
      } else {
        setError(errorMsg);
      }
    }
  } finally {
    setLoading(false);
  }
};

  const handleForgotPassword = async () => {
    const idNumber = form.getFieldValue('idNumber');
    
    if (!idNumber) {
      Modal.info({
        title: 'Password Help',
        content: 'Please enter your ID Number first, then click "Request Password" to have it sent to your email.',
      });
      return;
    }

    setPasswordLoading(true);
    
    try {
      const response = await axiosInstance.post('/GuestLogin/forgot-password', {
        idNumber: idNumber,
        hotelId: Number(hotelId),
      });

      if (response.data.success) {
        notify.success(`Password sent to ${response.data.emailSentTo}`);
        setForgotPasswordMode(true);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to send password. Please try again.';
      
      if (err.response?.data?.error === 'NoEmail') {
        Modal.error({
          title: 'No Email Found',
          content: 'We could not find an email address for your account. Please contact reception for assistance.',
        });
      } else {
        notify.error(errorMsg);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRequestPasswordByBooking = () => {
    Modal.confirm({
      title: 'Request Password by Booking Number',
      content: (
        <div style={{ marginTop: 16 }}>
          <Text>Enter your Booking Number to receive your password via email:</Text>
          <Input 
            placeholder="Booking Number" 
            style={{ marginTop: 8 }}
            id="bookingNumberInput"
          />
        </div>
      ),
      onOk: async () => {
        const bookingNo = document.getElementById('bookingNumberInput')?.value;
        if (!bookingNo) {
          notify.error('Please enter your Booking Number');
          return;
        }

        setPasswordLoading(true);
        try {
          const response = await axiosInstance.post('/GuestLogin/request-password-email', {
            bookingNo: bookingNo,
            hotelId: Number(hotelId),
          });

          if (response.data.success) {
            notify.success(`Password sent to ${response.data.emailSentTo}`);
          }
        } catch (err) {
          const errorMsg = err.response?.data?.message || 'Failed to send password. Please try again.';
          notify.error(errorMsg);
        } finally {
          setPasswordLoading(false);
        }
      }
    });
  };

  const handlePasswordHint = async () => {
    const idNumber = form.getFieldValue('idNumber');
    
    if (!idNumber) {
      Modal.info({
        title: 'Password Hint',
        content: 'Your password was emailed to you at check-in. Use "Forgot password" to receive it again.',
      });
      return;
    }

    try {
      const response = await axiosInstance.get(`/GuestLogin/hint/${idNumber}`);
      Modal.info({
        title: 'Password Hint',
        content: (
          <div>
            <p><strong>{response.data.hint}</strong></p>
          </div>
        ),
        width: 500,
      });
    } catch (err) {
      Modal.info({
        title: 'Password Hint',
        content: 'Your password is the last 4 characters of your Booking Number. If you forgot your Booking Number, please contact reception.',
      });
    }
  };

  const handleContactReception = () => {
    Modal.info({
      title: 'Contact Reception',
      content: (
        <div>
          <p>Please visit the front desk or call:</p>
          <p style={{ textAlign: 'center', fontSize: '18px', fontWeight: 'bold', margin: '16px 0' }}>
            <PhoneOutlined /> Extension 0
          </p>
          <p>Our staff will be happy to assist you with your login.</p>
        </div>
      ),
      width: 400,
    });
  };

  const renderLoginForm = () => (
    <>
      <div>
        <LockOutlined style={{ fontSize: 32, color: 'var(--hms-accent)', marginBottom: 8 }} />
        <Title level={3} style={{ marginBottom: 8 }}>Guest Login</Title>
        <Text type="secondary">
          Enter your ID Number and password to access room services
        </Text>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError('')}
        />
      )}

      <Form
        form={form}
        name="guestLogin"
        onFinish={onFinish}
        autoComplete="off"
        style={{ width: '100%' }}
        size="large"
      >
        <Form.Item
          name="idNumber"
          rules={[
            { required: true, message: 'Please enter your ID Number' },
            { min: 3, message: 'ID Number must be at least 3 characters' }
          ]}
        >
          <Input
            prefix={<IdcardOutlined />}
            placeholder="ID Number"
            autoComplete="username"
            disabled={passwordLoading}
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            { required: true, message: 'Please enter your password' },
            { min: 4, message: 'Password must be 4 characters' },
            { max: 4, message: 'Password must be 4 characters' }
          ]}
        >
          <Input.Password
            prefix={<KeyOutlined />}
            placeholder="Password (last 4 digits of Booking Number)"
            autoComplete="current-password"
            maxLength={4}
            disabled={passwordLoading}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={passwordLoading}
            block
            style={{ height: 45 }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Form.Item>
      </Form>
    </>
  );

  const renderPasswordSentConfirmation = () => (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <MailOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
      <Title level={3} style={{ color: '#52c41a', marginBottom: 8 }}>
        Password Sent!
      </Title>
      <Text type="secondary">
        Your password has been sent to your registered email address.
      </Text>
      <div style={{ marginTop: 24 }}>
        <Button 
          type="primary" 
          onClick={() => setForgotPasswordMode(false)}
          style={{ marginRight: 8 }}
        >
          Back to Login
        </Button>
        <Button 
          onClick={handleContactReception}
        >
          Still Need Help?
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={420}
      closable={!passwordLoading}
      maskClosable={!passwordLoading}
      confirmLoading={passwordLoading}
    >
      <Card
        style={{ border: 'none' }}
        bodyStyle={{ padding: 24 }}
        loading={passwordLoading}
      >
        {passwordLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Sending password to your email...</Text>
            </div>
          </div>
        ) : forgotPasswordMode ? (
          renderPasswordSentConfirmation()
        ) : (
          <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }} size="large">
            {renderLoginForm()}

            <Divider plain>Need help logging in?</Divider>

            <Row gutter={[8, 8]} justify="center">
              <Col span={24} style={{ marginBottom: 8 }}>
                <Button 
                  type="link" 
                  icon={<MailOutlined />}
                  onClick={handleForgotPassword}
                  style={{ padding: '4px 0' }}
                  block
                >
                  Request Password via Email
                </Button>
              </Col>
                
            </Row>

            <div style={{ fontSize: '12px', color: '#999', marginTop: 8 }}>
              <Text type="secondary">
                Your password is automatically generated from your booking details
              </Text>
            </div>
          </Space>
        )}
      </Card>
    </Modal>
  );
};

export default GuestLogin;